import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  whatsappRequestSignupOtp,
  whatsappVerifySignupOtp,
  whatsappCompleteSignup,
} from "@/lib/whatsapp.functions";
import {
  getAffiliateApplyContext,
  submitAffiliateApplication,
  CONSENT_LABELS,
  type ApplyContext,
} from "@/lib/affiliate/apply.functions";
import { sanitizeAgreementHtml } from "@/lib/affiliate/sanitize-html";

export const Route = createFileRoute("/affiliate/apply")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Become a TruWe partner. Affiliate program" },
      {
        name: "description",
        content:
          "Apply to join the TruWe affiliate program. Verify your WhatsApp number to start your application and earn commission on referred orders.",
      },
      { property: "og:title", content: "Become a TruWe partner. Affiliate program" },
      {
        property: "og:description",
        content: "Join the TruWe affiliate program and earn commission on referred orders.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://truwe.in/affiliate/apply" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/affiliate/apply" }],
  }),

  component: AffiliateApplyPage,
});

// ---------------------------------------------------------------------------

function AffiliateApplyPage() {
  const [ctx, setCtx] = useState<ApplyContext | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchCtx = useServerFn(getAffiliateApplyContext);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchCtx();
      setCtx(r);
    } catch {
      setCtx({ ok: false, reason: "unverified" });
    } finally {
      setLoading(false);
    }
  }, [fetchCtx]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) {
    return <Shell><p className="text-sm text-muted-foreground">Loading…</p></Shell>;
  }
  if (!ctx || !ctx.ok) {
    // Every negative case (unverified, expired, invalid) drops back to the
    // OTP gate. The application form is NOT rendered until we hold a fresh
    // WhatsApp-verified session.
    return (
      <Shell>
        <OtpGate onVerified={refresh} />
      </Shell>
    );
  }
  return (
    <Shell>
      <ApplicationForm
        verifiedPhone={ctx.verifiedPhone}
        termsHtml={ctx.terms.content_html}
        termsVersion={ctx.terms.version}
        termsEffective={ctx.terms.effective_at}
        onExpired={refresh}
      />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto max-w-xl px-6 py-16">
      <h1 className="text-3xl font-semibold tracking-tight">Become a TruWe partner</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Verify your WhatsApp number to start your application. Approved
        partners are contacted by email within 5 business days.
      </p>
      <div className="mt-8">{children}</div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// OTP gate — the ONLY thing shown before verification.
// Reuses the platform-wide WhatsApp OTP server functions.
// ---------------------------------------------------------------------------

type OtpStage = "phone" | "code" | "email";

function OtpGate({ onVerified }: { onVerified: () => void }) {
  const [stage, setStage] = useState<OtpStage>("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [signupTicket, setSignupTicket] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState("");
  const [fullName, setFullName] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef<number | null>(null);

  const requestOtp = useServerFn(whatsappRequestSignupOtp);
  const verifyOtpFn = useServerFn(whatsappVerifySignupOtp);
  const completeSignupFn = useServerFn(whatsappCompleteSignup);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = window.setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [cooldown]);

  async function establishSession(hashedToken: string) {
    const { error } = await supabase.auth.verifyOtp({
      token_hash: hashedToken,
      type: "magiclink",
    });
    if (error) {
      toast.error("Sign-in failed. Please try again.");
      return;
    }
    toast.success("Verified");
    onVerified();
  }

  async function sendCode() {
    if (busy || cooldown > 0) return;
    setBusy(true);
    try {
      const r = await requestOtp({ data: { phone } });
      if (r.ok) {
        toast.success("Code sent on WhatsApp");
        setStage("code");
        setCode("");
        setCooldown(r.cooldownSeconds ?? 45);
      } else if (r.error === "invalid_phone") {
        toast.error("Enter a valid Indian mobile number");
      } else if (r.error === "rate_limited") {
        const wait = r.retryAfterSec ?? 60;
        toast.error(`Please wait ${wait}s before requesting another code`);
        setCooldown(wait);
      } else {
        toast.error("Couldn't send the code. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await verifyOtpFn({ data: { phone, code } });
      if (r.ok) {
        await establishSession(r.hashedToken);
        return;
      }
      if (r.error === "email_required" && "signupTicket" in r && r.signupTicket) {
        setSignupTicket(r.signupTicket);
        setStage("email");
        return;
      }
      toast.error(
        r.error === "invalid" ? "Incorrect code." :
        r.error === "expired" ? "Code expired. Resend a new one." :
        r.error === "too_many_attempts" ? "Too many attempts. Resend a new code." :
        "Couldn't verify. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function completeSignup() {
    if (busy || !signupTicket) return;
    setBusy(true);
    try {
      const r = await completeSignupFn({
        data: { signupTicket, email: emailInput.trim() || undefined, fullName: fullName || undefined },
      });
      if (!r.ok) {
        toast.error(r.error === "ticket_invalid" ? "Session expired, request a new code." : "Sign-in failed.");
        if (r.error === "ticket_invalid") { setStage("phone"); setSignupTicket(null); }
        return;
      }
      await establishSession(r.hashedToken);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-lg font-medium">Verify your WhatsApp number</h2>
      {stage === "phone" && (
        <div className="mt-4 space-y-3">
          <Label htmlFor="phone">Mobile number</Label>
          <Input id="phone" inputMode="tel" placeholder="10-digit mobile" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <Button onClick={sendCode} disabled={busy || cooldown > 0 || phone.trim().length < 10} className="w-full">
            {cooldown > 0 ? `Resend in ${cooldown}s` : busy ? "Sending…" : "Send code on WhatsApp"}
          </Button>
        </div>
      )}
      {stage === "code" && (
        <div className="mt-4 space-y-3">
          <Label htmlFor="code">Enter the 6-digit code</Label>
          <Input id="code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          <Button onClick={verify} disabled={busy || code.length !== 6} className="w-full">
            {busy ? "Verifying…" : "Verify"}
          </Button>
          <button type="button" className="text-xs text-muted-foreground underline" onClick={sendCode} disabled={cooldown > 0}>
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
          </button>
        </div>
      )}
      {stage === "email" && (
        <div className="mt-4 space-y-3">
          <Label htmlFor="email">Email for account &amp; receipts</Label>
          <Input id="email" type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} />
          <Label htmlFor="name">Your name</Label>
          <Input id="name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          <Button onClick={completeSignup} disabled={busy || !emailInput.trim()} className="w-full">
            {busy ? "Finishing…" : "Continue"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Application form — rendered ONLY after the OTP gate passes.
// The verified phone is server-derived and shown read-only; the form has no
// editable phone field.
// ---------------------------------------------------------------------------

function ApplicationForm({
  verifiedPhone,
  termsHtml,
  termsVersion,
  termsEffective,
  onExpired,
}: {
  verifiedPhone: string;
  termsHtml: string;
  termsVersion: string;
  termsEffective: string;
  onExpired: () => void;
}) {
  const submit = useServerFn(submitAffiliateApplication);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const termsBoxRef = useRef<HTMLDivElement | null>(null);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    address_line: "",
    city: "",
    state: "",
    pincode: "",
    creator_type: "content_creator",
    primary_social_url: "",
    niche: "",
    follower_range: "1k_10k",
    audience_geography: "india",
    promotion_plan: "",
    preferred_code: "",
    consent_agreement: false,
    consent_privacy: false,
    consent_asci: false,
  });

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((s) => ({ ...s, [k]: v }));
  }

  function onTermsScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    // 24px slack for sub-pixel rounding.
    if (el.scrollHeight - (el.scrollTop + el.clientHeight) < 24) {
      setScrolledToEnd(true);
    }
  }

  const allConsents =
    form.consent_agreement && form.consent_privacy && form.consent_asci;
  const canSubmit = scrolledToEnd && allConsents && !busy;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      // NOTE: no phone in the payload — the server derives it from session.
      // We also never send version/hash/timestamps — server-derived only.
      const r = await submit({
        data: {
          ...form,
          user_agent:
            typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        },
      });
      if (r.ok) {
        toast.success(`Application received, ${r.receipt_number}`);
        void navigate({ to: "/affiliate/receipt", search: { n: r.receipt_number } });
        return;
      }
      if (r.reason === "expired" || r.reason === "unverified") {
        toast.error("Session expired. Please verify your WhatsApp number again.");
        onExpired();
        return;
      }
      if (r.reason === "phone_mismatch") {
        toast.error("Phone verification mismatch. Please verify again.");
        onExpired();
        return;
      }
      if (r.reason === "duplicate") {
        toast.error("An application with these details is already on file.");
        return;
      }
      if (r.reason === "consent_incomplete") {
        toast.error("Please tick all three consent boxes to continue.");
        return;
      }
      toast.error("Please review the form and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5 rounded-lg border border-border bg-card p-6">
      <div className="rounded-md bg-muted/40 px-3 py-2 text-sm">
        <span className="text-muted-foreground">Verified WhatsApp: </span>
        <span className="font-mono">{verifiedPhone}</span>
      </div>

      <Field label="Full name"><Input required maxLength={120} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></Field>
      <Field label="Email (contact / recovery only)"><Input type="email" required maxLength={254} value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
      <Field label="Address"><Input required maxLength={240} value={form.address_line} onChange={(e) => set("address_line", e.target.value)} /></Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="City"><Input required value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
        <Field label="State"><Input required value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
        <Field label="Pincode"><Input required inputMode="numeric" maxLength={6} value={form.pincode} onChange={(e) => set("pincode", e.target.value)} /></Field>
      </div>
      <Field label="Primary social URL (Instagram / YouTube / etc.)">
        <Input type="url" required value={form.primary_social_url} onChange={(e) => set("primary_social_url", e.target.value)} />
      </Field>
      <Field label="Niche"><Input required maxLength={120} value={form.niche} onChange={(e) => set("niche", e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Creator type">
          <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.creator_type} onChange={(e) => set("creator_type", e.target.value)}>
            {["influencer","content_creator","coach","clinician","community","publication","other"].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Follower range">
          <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.follower_range} onChange={(e) => set("follower_range", e.target.value)}>
            {["under_1k","1k_10k","10k_50k","50k_250k","250k_1m","over_1m"].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Audience geography">
        <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.audience_geography} onChange={(e) => set("audience_geography", e.target.value)}>
          {["india","india_diaspora","global","other"].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </Field>
      <Field label="How will you promote TruWe?">
        <textarea className="min-h-24 w-full rounded-md border border-input bg-background p-3 text-sm" required maxLength={1000} value={form.promotion_plan} onChange={(e) => set("promotion_plan", e.target.value)} />
      </Field>
      <Field label="Preferred coupon code (optional)">
        <Input maxLength={20} value={form.preferred_code} onChange={(e) => set("preferred_code", e.target.value.toUpperCase())} />
      </Field>

      {/* Terms & Conditions, informational only. The binding Affiliate
          Agreement is executed physically (on paper) after approval, so the
          online form no longer gates on scroll-through. Consent checkboxes
          below acknowledge the T&C and privacy/ASCI commitments. */}
      <div className="rounded-md border border-border">
        <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs">
          <span>
            <strong>Affiliate Partner Terms &amp; Conditions</strong> {termsVersion} · effective {new Date(termsEffective).toDateString()}
          </span>
          <a href="/policies/affiliate-terms" target="_blank" rel="noreferrer" className="text-primary underline">
            Open in new tab
          </a>
        </div>
        <div
          ref={termsBoxRef}
          onScroll={onTermsScroll}
          className="max-h-64 overflow-y-auto px-4 py-3 text-xs leading-relaxed"
          // Server-authored canonical HTML. Re-sanitized at render time as
          // defense-in-depth (see finding 13) in case a compromised session or
          // a future regression ever wrote unsanitized content upstream.
          dangerouslySetInnerHTML={{ __html: sanitizeAgreementHtml(termsHtml) }}
        />
        <div className="border-t border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          These Terms &amp; Conditions are for your reference. The binding
          Affiliate Agreement will be signed <strong>physically on paper</strong>{" "}
          after your application is reviewed and approved.
        </div>
      </div>

      <fieldset className="space-y-3">
        <legend className="sr-only">Required acknowledgements</legend>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={form.consent_agreement} onChange={(e) => set("consent_agreement", e.target.checked)} required />
          <span>{CONSENT_LABELS.agreement}</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={form.consent_privacy} onChange={(e) => set("consent_privacy", e.target.checked)} required />
          <span>{CONSENT_LABELS.privacy}</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={form.consent_asci} onChange={(e) => set("consent_asci", e.target.checked)} required />
          <span>{CONSENT_LABELS.asci}</span>
        </label>
      </fieldset>

      <Button type="submit" disabled={!canSubmit} className="w-full">
        {busy ? "Submitting…" : "Submit application"}
      </Button>
      <p className="text-[11px] text-muted-foreground">
        Submitting this application is not a binding partnership. If approved,
        the TruWe team will contact you to sign the Affiliate Agreement in
        person / on paper before your affiliate account is activated.
      </p>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
